'use client';

import { Suspense } from 'react';

import dynamic from 'next/dynamic';

import type { BioContext } from '@ps/functions/bio/bioContext';

import type { GetYoursVariation } from './GetYoursVariation';

/**
 * Lazy mount for {@link BioGetYoursBanner}. The banner is a non-critical floating
 * overlay whose visibility depends entirely on client state (auth + scroll), so
 * `ssr: false` keeps its bundle out of the initial visitor download and defers it
 * to a post-hydration chunk. {@link ReadonlyBioPage} renders this ONLY when the
 * creator hasn't hidden the banner, so a "removed" banner triggers no import at
 * all.
 */
const BioGetYoursBanner = dynamic(
  () => import('./BioGetYoursBanner').then(mod => mod.BioGetYoursBanner),
  { ssr: false },
);

export function BioGetYoursBannerMount(props: {
  sellerPasscode?: string;
  bioContext: BioContext;
  variation?: GetYoursVariation;
}) {
  // Local boundary REQUIRED: this bare `ssr: false` mount renders inside the
  // `(profile)` route's Suspense; without its own boundary the first-mount
  // chunk suspension bubbles up and flashes the whole bio white before the
  // banner resolves. Fallback `null` — the banner is a floating overlay that
  // shows nothing until client state decides to. See [[reference_lazy_modal_suspense_bubbling]].
  return (
    <Suspense fallback={null}>
      <BioGetYoursBanner {...props} />
    </Suspense>
  );
}
